/**
 * An alternative, compact — the same radio grammar as the card above it and the
 * picker behind it.
 *
 * Tapping one promotes it into the card, reason and figure included, so whatever
 * the CTA names always has its justification on screen.
 */

import {
  figureOf,
  figureTitle,
  rowMeta,
} from '@personal-trainer-app/shared/src/utils/workout/start-options';

import type { WorkoutPickRowProps } from './StartWorkoutSheet.types';

import { CopyRuns } from '@/components/ui/CopyRuns';
import { MuscleFigure } from '@/components/ui/MuscleMap';
import { Text } from '@/components/ui/primitives';
import { Radio } from '@/components/ui/Radio';

export function WorkoutPickRow({
  workout,
  selected,
  onSelect,
  name,
  note,
  tag,
  tagTone = 'accent',
  caution,
}: WorkoutPickRowProps): React.JSX.Element {
  const { model, face } = figureOf(workout);

  return (
    <label
      className={`flex min-h-16 cursor-pointer items-center gap-3 rounded-lg border-[1.5px] py-2.5 pl-2.5 pr-3 transition-colors duration-fast ${
        selected
          ? 'border-stroke-accent bg-sakura-wash'
          : 'border-stroke-subtle bg-contrast-700 hover:border-stroke-strong'
      }`}
      data-testid="start-workout-pick-row"
    >
      <div className="grid w-10 flex-none place-items-center">
        <div className="w-[18px]">
          <MuscleFigure
            face={face}
            model={model}
            selectedId={null}
            interactive={false}
            onSelect={() => undefined}
            title={figureTitle(workout)}
          />
        </div>
      </div>

      <div className="min-w-0 flex-1">
        <Text
          as="div"
          variant="body-md"
          className="truncate font-semibold text-neutral-100"
        >
          {workout.name}
        </Text>

        {tag && (
          <Text
            as="div"
            variant="label-xs"
            color={tagTone === 'warning' ? 'warning' : 'accent'}
            className="mt-0.5 font-semibold uppercase tracking-wider"
          >
            {tag}
          </Text>
        )}

        {note && (
          <Text
            as="div"
            variant="body-sm"
            color="secondary"
            className="mt-0.5 truncate"
          >
            {note}
          </Text>
        )}

        <Text
          as="div"
          variant="body-sm"
          color="muted"
          className="mt-0.5 truncate"
        >
          <CopyRuns
            segments={rowMeta(workout)}
            figureClassName="font-mono text-neutral-300"
          />
        </Text>

        {caution && (
          <div className="mt-0.5 flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="h-1.5 w-1.5 flex-none rounded-full bg-warning-400"
            />
            <Text
              as="span"
              variant="body-sm"
              color="muted"
              className="truncate"
            >
              {caution}
            </Text>
          </div>
        )}
      </div>

      <div className="grid w-6 flex-none place-items-center">
        <Radio
          name={name}
          value={String(workout.id)}
          checked={selected}
          onChange={() => onSelect(workout.id)}
          aria-label={workout.name}
        />
      </div>
    </label>
  );
}

export default WorkoutPickRow;
